import React, {useEffect} from "react";
import { useSelector } from 'react-redux'; 
import {
  Box, 
  Grid, 
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import RegisterStyles from "../../assets/css/register.styles";  
import { GeneralStyles } from "../../assets/css/general.styles";
import CustomLabeledTextField from "../../components/customTextField/customLabeledTextField"; 
import theme from "../../themes/index.en";
import { ValidatableComponent } from "./validatableComponent";
import { InvestorModel } from "../../models/models/investorModel";
import { RegisterTouched } from "./registerTouched";
import CustomLabeledSelect from "../../components/customSelect/customLabeledSelect";  
import { RootState } from "../../store/store";
import { getInvestmentObjective, getPreferredProducts, getRiskTolerance } from "../../reducers/lookupReducer";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { InvestmentObjective, LookupEnum, PreferredProducts } from "../../constants/enums";
import { InvestorInvestmentPreferenceModel } from "../../models/models/investorInvestmentPreferenceModel";

const InvestmentPreference: ValidatableComponent = ({
  formData,
  setFormData,
  touched,
  setTouched,
  isRTL,
  t
}) => { 
   const dispatch = useAppDispatch();

  const investmentObjectiveList = useSelector((state: RootState) => state.lookup.investmentObjective);
  const riskToleranceList = useSelector((state: RootState) => state.lookup.riskTolerance);
  const preferredProductsList = useSelector((state: RootState) => state.lookup.preferredProducts);
  
  useEffect(() => {  
    dispatch(getInvestmentObjective({lookupId: LookupEnum.InvestmentObjective}));
    dispatch(getRiskTolerance({lookupId: LookupEnum.RiskTolerance}));
    dispatch(getPreferredProducts({lookupId: LookupEnum.PreferredProducts}));
  },[]); 

  const typeOfInvestors= [
    { label: "qualifiedProfessionalInvestor", field: "isQualifiedProfessionalInvestor" },
    { label: "professionalInvestor", field: "isProfessionalInvestor" },
    { label: "retailInvestor", field: "isRetailInvestor" },
  ]
  
  const investingExperience= [
    { label: "securities", field: "hasPreviousSecurities" },
    { label: "sukok", field: "hasPreviousSukok" },
    { label: "bonds", field: "hasPreviousBonds" },
    { label: "realEstate", field: "hasPreviousRealState" },
    { label: "investmentFunds", field: "hasPreviousInvestmentFunds" },
    { label: "otherPleaseSpecify", field: "hasOtherPreviousExperience" },
  ] 
 
  const riskAwarenessQuestions = [
    {
      label: "highLiquidityRiskUnderstand",
      field: "hasHighLiquidityRiskUnderstand",
    },
    {
      label: "investmentLossUnderstand",
      field: "hasInvestmentLossUnderstand",
    },
    {
      label: "uncertainPayoutsUnderstand",
      field: "hasUncertainPayoutsUnderstand",
    },
    {
      label: "withdrawalRightsUnderstand",
      field: "hasWithdrawalRightsUnderstand",
      note: "withdrawalRightsUnderstandNote", 
    },
  ];

  const investorLimitsQuestions = [
    {
      label:"investProfessionalLimit",
      field: "canInvestProfessionalLimit",
    },
    {
      label: "investRetailLimit",
      field: "canInvestRetailLimit",
    },
    {
      label: "investRetailOfferingLimit",
      field: "canInvestRetailOfferingLimit",
    },
    {
      label: "limitsWithdrawalRightsUnderstand",
      field: "hasLimitsWithdrawalRightsUnderstand",
      note: "limitsWithdrawalRightsUnderstandNote",
    },
  ];

  const confirmationsQuestions = [
    {
      label:"riskComprehensionConfirmation",
      field: "hasRiskComprehensionConfirmation",
    },
    {
      label: "documentReviewedConfirmation",
      field: "hasDocumentReviewedConfirmation",
    },
    {
      label: "contentsUnderstoodConfirmation",
      field: "hasContentsUnderstoodConfirmation",
    },
    {
      label: "professionalLimitConfirmation",
      field: "hasProfessionalLimitConfirmation",
    },
    {
      label: "retailOfferingLimitConfirmation",
      field: "hasRetailOfferingLimitConfirmation",
    },
    {
      label: "retailLimitConfirmation",
      field: "hasRetailLimitConfirmation",
    },
  ];

  const handleCheckboxChange = (
    field: string,
    groupFields: string[],
    checked: boolean
  ) => {
    const updatedData = groupFields.reduce((acc, groupField) => {
      acc[groupField] = groupField === field ? checked : false;
      return acc;
    }, {} as Record<string, boolean>); 
    const resetFields =
    field === "isRetailInvestor" || field === "isProfessionalInvestor"
      ? {
        hasHighLiquidityRiskUnderstand: null,
        hasInvestmentLossUnderstand: null,
        hasUncertainPayoutsUnderstand: null,
        hasWithdrawalRightsUnderstand: null, 
        canInvestProfessionalLimit: null, 
        canInvestRetailLimit: null, 
        canInvestRetailOfferingLimit: null, 
        hasLimitsWithdrawalRightsUnderstand: null,  
        hasRiskComprehensionConfirmation: null,  
        hasDocumentReviewedConfirmation: null,  
        hasContentsUnderstoodConfirmation: null,  
        hasProfessionalLimitConfirmation: null,  
        hasRetailOfferingLimitConfirmation: null,  
        hasRetailLimitConfirmation: null,  
        isInfoAcknowledgmentConfirmed: false,   
        }
      : {};
    setFormData((prevData) => ({
      ...prevData,
      investmentPreference: {
        ...prevData.investmentPreference,
        ...updatedData,
        ...resetFields,
      },
    }))
  };

  
  return (
    <Box
    display="flex"
    flexDirection="column"
    gap={2}
    mb={2}
    pl={5}
    sx={{
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr", 
      marginRight: '50px', 
    }}
  >
      <Box sx={RegisterStyles.boxStyle}>
        <Grid container item xs={12} spacing={2}>
          <Typography variant="h6" gutterBottom p={1} sx={{ fontWeight: "bold" }}>
            {t('previousInvestingExperience')}
          </Typography>  
          {touched.investingExperience && (
          <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
            {t('chooseOneOption') }
          </Typography>
        )} 
          <Grid container item xs={12} spacing={2}>
            {investingExperience.map(({ label, field }) => (
                field === "hasOtherPreviousExperience" ? ( 
                   <Grid container item xs={12} sm={6} md={6} spacing={1}> 
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                                <Checkbox
                                  checked={!!formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel]} 
                                  style={GeneralStyles.checkBoxStyle}
                                  onChange={(e) => handleCheckboxChange(field,investingExperience.map((option) => option.field), e.target.checked)}
                                  name={field}
                                />
                            }
                            label={t(`${label}`)}
                            />
                        </Grid> 
                        {formData.investmentPreference.hasOtherPreviousExperience &&(
                          <Grid item xs={12} sm={8}>
                              <CustomLabeledTextField
                              label={t('otherPreviousInvesting')}
                              name="otherPreviousExperience"
                              type="text"
                              value={formData.investmentPreference.otherPreviousExperience} 
                              onChange={(e) => 
                                  setFormData((prevData) => ({
                                    ...prevData,
                                    investmentPreference: {
                                      ...prevData.investmentPreference,
                                      otherPreviousExperience:  e.target.value ,
                                    },
                                  }))
                              }
                              required={formData.investmentPreference.hasOtherPreviousExperience ? true : false}
                              disabled={formData.investmentPreference.hasOtherPreviousExperience ? false : true}
                              error={touched.otherPreviousExperience && formData.investmentPreference.otherPreviousExperience.trim() === "" && formData.investmentPreference.hasOtherPreviousExperience}
                              helperText={
                                  touched.otherPreviousExperience && formData.investmentPreference.otherPreviousExperience.trim() === "" && formData.investmentPreference.hasOtherPreviousExperience
                                  ? t('mandatoryField')
                                  : ""
                              } 
                              />
                          </Grid>
                        )} 
                    </Grid>                 
                ):(
                    <Grid item xs={12} sm={6} key={field}>
                        <FormControlLabel
                        control={
                            <Checkbox
                              checked={!!formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel]} 
                              style={GeneralStyles.checkBoxStyle}
                              onChange={(e) => handleCheckboxChange(field,investingExperience.map((option) => option.field), e.target.checked)}
                              name={field}
                            />
                        }
                        label={t(`${label}`)}
                        />
                   </Grid>
                )
            ))}
          </Grid>
        </Grid>
      </Box> 
      <Box sx={RegisterStyles.boxStyle}>
        <Grid container item xs={12} spacing={2}>
          <Typography variant="h6" gutterBottom p={1} sx={{ fontWeight: "bold" }}>
            {t('typeOfInvestor')}
          </Typography> 
          {touched.typeOfInvestors && (
          <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
            {t('chooseOneOption') }
          </Typography>
        )} 
          {typeOfInvestors.map(({ label, field }) => (
            <Grid item xs={12} key={field}>
              <FormControlLabel
                control={
                  <Checkbox 
                      checked={!!formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel]}
                      style={GeneralStyles.checkBoxStyle}
                      onChange={(e) => handleCheckboxChange(field,typeOfInvestors.map((option) => option.field), e.target.checked)}
                      name={field}
                  />
                }
                label={
                  <Typography variant="body1" sx={RegisterStyles.paragraphStyle}>
                    <strong style={{marginLeft: '10px'}}>{t(`${label}`)}</strong>
                    {field === "isQualifiedProfessionalInvestor" && t('qualifiedPerson')}
                    {field === "isProfessionalInvestor" && t('profesionalPerson')}
                    {field === "isRetailInvestor" && t('retailedPerson')}
                  </Typography>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={RegisterStyles.boxStyle}> 
        <Grid container item xs={12} sm={12}  mt={1} md={12} spacing={2}> 
            <Grid container item xs={12} sm={4} spacing={1}> 
              <Grid item xs={12} sm={formData.investmentPreference.investmentObjectiveId === InvestmentObjective.Other ? 4 : 12}>
                  <CustomLabeledSelect
                      label={t('investmentObjectives')}
                      isRTL={isRTL}
                      name="investmentObjectiveId"
                      type="text"
                      SelectProps={{
                        children: Array.isArray(investmentObjectiveList.data) ? (
                          investmentObjectiveList.data.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {isRTL ? item.valueAr : item.valueEn}
                            </MenuItem>
                          ))
                        ) : null,
                      }}
                      value={formData.investmentPreference.investmentObjectiveId || ""}
                      onChange={(e) => 
                        setFormData((prevData) => ({
                          ...prevData,
                          investmentPreference: {
                            ...prevData.investmentPreference,
                            investmentObjectiveId:  e.target.value as number,
                          },
                        }))
                    }
                      required={true}
                      multiple={false}
                      error={touched.investmentObjectiveId && formData.investmentPreference.investmentObjectiveId === null}
                      helperText={
                        touched.investmentObjectiveId && formData.investmentPreference.investmentObjectiveId === null
                          ? `${t('chooseOneOption')}`
                          : ""
                      }
                    />
              </Grid> 
            {formData.investmentPreference.investmentObjectiveId === InvestmentObjective.Other &&( 
              <Grid item xs={12} sm={8}>
                      <CustomLabeledTextField 
                      label={t('otherInvestmentObjectives')}
                      isRTL={isRTL}
                      name="otherInvestmentObjective"
                      type="text"
                      value={formData.investmentPreference.otherInvestmentObjective} 
                      onChange={(e) => 
                        setFormData((prevData) => ({
                          ...prevData,
                          investmentPreference: {
                            ...prevData.investmentPreference,
                            otherInvestmentObjective: e.target.value ,
                          },
                        }))
                    }
                      required={true} 
                      error={touched.otherInvestmentObjective && formData.investmentPreference.otherInvestmentObjective.trim() === "" && formData.investmentPreference.investmentObjectiveId === InvestmentObjective.Other}
                      helperText={
                          touched.otherInvestmentObjective && formData.investmentPreference.otherInvestmentObjective.trim() === "" && formData.investmentPreference.investmentObjectiveId === InvestmentObjective.Other
                          ? `${t('mandatoryField')}` 
                          : ""
                      } 
                      />
              </Grid> 
            )}
            </Grid> 
            <Grid item xs={12} sm={6} md={4}>
                <CustomLabeledSelect
                  label={t('riskTolerance')}
                  isRTL={isRTL}
                  name="riskToleranceId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(riskToleranceList.data) ? (
                      riskToleranceList.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {isRTL ? item.valueAr : item.valueEn}
                        </MenuItem>
                      ))
                    ) : null,
                  }}
                  value={formData.investmentPreference.riskToleranceId || ""}
                  onChange={(e) => 
                    setFormData((prevData) => ({
                      ...prevData,
                      investmentPreference: {
                        ...prevData.investmentPreference,
                        riskToleranceId:  e.target.value as number,
                      },
                    }))
                }
                  required={true}
                  multiple={false}
                  error={touched.riskToleranceId && formData.investmentPreference.riskToleranceId === null}
                  helperText={
                    touched.riskToleranceId && formData.investmentPreference.riskToleranceId === null
                      ? `${t('chooseOneOption')}`
                      : ""
                  }
                />
            </Grid> 
            <Grid container item xs={12} sm={4} spacing={1}> 
              <Grid item xs={12} sm={formData.investmentPreference.preferredProductsId === PreferredProducts.Other ? 4 : 12}>
                  <CustomLabeledSelect 
                      label={t('preferredProducts')}
                      isRTL={isRTL}
                      name="preferredProductsId"
                      type="text"
                      SelectProps={{
                        children: Array.isArray(preferredProductsList.data) ? (
                          preferredProductsList.data.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {isRTL ? item.valueAr : item.valueEn}
                            </MenuItem>
                          ))
                        ) : null,
                      }}
                      value={formData.investmentPreference.preferredProductsId || ""}
                      onChange={(e) => 
                        setFormData((prevData) => ({
                          ...prevData,
                          investmentPreference: {
                            ...prevData.investmentPreference,
                            preferredProductsId:  e.target.value as number,
                          },
                        }))
                    }
                      required={true}
                      multiple={false}
                      error={touched.preferredProductsId && formData.investmentPreference.preferredProductsId === null}
                      helperText={
                        touched.preferredProductsId && formData.investmentPreference.preferredProductsId === null
                          ? `${t('chooseOneOption')}`
                          : ""
                      }
                    />
              </Grid> 
            {formData.investmentPreference.preferredProductsId === PreferredProducts.Other &&( 
              <Grid item xs={12} sm={8}>
                      <CustomLabeledTextField
                      label="Other Preferred Products"
                      name="otherPreferredProducts"
                      type="text"
                      value={formData.investmentPreference.otherPreferredProducts} 
                      onChange={(e) => 
                        setFormData((prevData) => ({
                          ...prevData,
                          investmentPreference: {
                            ...prevData.investmentPreference,
                            otherPreferredProducts:  e.target.value ,
                          },
                        }))
                    }
                      required={true} 
                      error={touched.otherPreferredProducts && formData.investmentPreference.otherPreferredProducts.trim() === "" && formData.investmentPreference.preferredProductsId === PreferredProducts.Other}
                      helperText={
                          touched.otherPreferredProducts && formData.investmentPreference.otherPreferredProducts.trim() === "" && formData.investmentPreference.preferredProductsId === PreferredProducts.Other
                          ? `${t('mandatoryField')}` 
                          : ""
                      } 
                      />
              </Grid> 
            )}
            </Grid> 
        </Grid>
      </Box>  
      {(formData.investmentPreference.isRetailInvestor || formData.investmentPreference.isProfessionalInvestor) && (
        <> 
          <Box sx={RegisterStyles.boxColoredStyle}>
            <Grid container item xs={12} spacing={2}>  
              <Grid item xs={12}> 
                <Typography variant="body1" sx={RegisterStyles.paragraphStyle}>
                    {t('professionalRetailform')}
                </Typography> 
              </Grid>
            </Grid>
          </Box> 
          <Box sx={RegisterStyles.boxStyle}>
            <Typography variant="h6" gutterBottom p={1} sx={{ fontWeight: "bold" }}> 
              {t('doYouUnderstand')}
            </Typography>
            {touched.riskAwarenessQuestions && (
              <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
                {t('chooseOneOption') }
              </Typography>
            )} 
            <Grid container spacing={2}>
              {riskAwarenessQuestions.map(({ label, field, note }) => (
                <Grid container item xs={12} spacing={1} key={field}>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                      {t(`${label}`)}
                    </Typography>
                  </Grid>
                  {note && (
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ fontStyle: "italic", color: "gray" }}
                      > 
                        {t(`${note}`)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle}
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === true}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference,
                                [field]: e.target.checked ? true : null,
                              },
                            }))
                          }
                        />
                      }
                      label={t(`${'yes'}`)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle}
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === false}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference,
                                [field]: e.target.checked ? false : null,
                              },
                            }))
                          }
                        />
                      }
                      label={t(`${'no'}`)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={RegisterStyles.boxStyle}>
            <Typography variant="h6" gutterBottom p={1} sx={{ fontWeight: "bold" }}>
                {t('investorlimits')}
            </Typography>
            {touched.investorLimitsQuestions && (
              <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
                {t('chooseOneOption') }
              </Typography>
            )}
            <Grid container spacing={2}>
              {investorLimitsQuestions.map(({ label, field, note }) => (
                <Grid container item xs={12} spacing={1} key={field}>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {t(`${label}`)}
                    </Typography>
                  </Grid>
                  {note && (
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ fontStyle: "italic", color: "gray" }}
                      >
                        {t(`${note}`)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle}
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === true}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference,
                                [field]: e.target.checked ? true : null,
                              },
                            }))
                          }
                        />
                      }
                       label={t(`${'yes'}`)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle} 
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === false}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference,
                                [field]: e.target.checked ? false : null,
                              },
                            }))
                          }
                        />
                      }
                       label={t(`${'no'}`)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={RegisterStyles.boxStyle}>
            <Typography variant="h6" gutterBottom p={1} sx={{ fontWeight: "bold" }}>
            {t('confirmThat') }
            </Typography>
            {touched.confirmationsQuestions && (
              <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
                {t('chooseOneOption') }
              </Typography>
            )}
            <Grid container spacing={2}>
              {confirmationsQuestions.map(({ label, field }) => (
                <Grid container item xs={12} spacing={1} key={field}>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {t(`${label}`)}
                    </Typography>
                  </Grid> 
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle}
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === true}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference, 
                                [field]: e.target.checked ? true : null,
                              },
                            }))
                          }
                        />
                      }
                       label={t(`${'yes'}`)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={GeneralStyles.checkBoxStyle}
                          checked={formData.investmentPreference[field as keyof InvestorInvestmentPreferenceModel] === false}
                          onChange={(e) =>
                            setFormData((prevData) => ({
                              ...prevData,
                              investmentPreference: {
                                ...prevData.investmentPreference,
                                [field]: e.target.checked ? false : null,
                              },
                            }))
                          }
                        />
                      }
                       label={t(`${'no'}`)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>
          {touched.riskAwarnessConfirmation && (
              <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
                {t('chooseOneOption') }
              </Typography>
            )}
          <Box sx={RegisterStyles.boxColoredStyle} display="flex" alignItems="flex-start" flex={1} mb={{ xs: 2, sm: 0 }}>
                <Checkbox size="small"  
                  checked={!!formData.investmentPreference.isInfoAcknowledgmentConfirmed}
                  style={GeneralStyles.checkBoxStyle}
                  onChange={(e) => 
                    setFormData((prevData) => ({
                      ...prevData,
                      investmentPreference: {
                        ...prevData.investmentPreference,
                        isInfoAcknowledgmentConfirmed:  e.target.checked ,
                      },
                    }))
                }
                />
            <Box>
              <Typography variant="body1" sx={RegisterStyles.paragraphStyle} >
               {t('confirmPreferences')}
              </Typography> 
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};


InvestmentPreference.validate = (formData: InvestorModel): RegisterTouched => {
  const newTouched = {
    typeOfInvestors: formData.investmentPreference.isQualifiedProfessionalInvestor === null ||
                formData.investmentPreference.isProfessionalInvestor=== null ||
                formData.investmentPreference.isRetailInvestor=== null,                   

    investingExperience: formData.investmentPreference.hasPreviousSecurities === null||
                formData.investmentPreference.hasPreviousSukok=== null ||
                formData.investmentPreference.hasPreviousBonds=== null ||
                formData.investmentPreference.hasPreviousRealState=== null ||
                formData.investmentPreference.hasPreviousInvestmentFunds=== null || 
                formData.investmentPreference.hasOtherPreviousExperience === null || 
                (formData.investmentPreference.hasOtherPreviousExperience === true && formData.investmentPreference.otherPreviousExperience.trim() === "" ), 

    preferredProductsId: formData.investmentPreference.preferredProductsId === null,
    riskToleranceId: formData.investmentPreference.riskToleranceId === null,
    investmentObjectiveId: formData.investmentPreference.investmentObjectiveId === null,
    otherInvestmentObjective: formData.investmentPreference.otherInvestmentObjective.trim() === "" && formData.investmentPreference.investmentObjectiveId === InvestmentObjective.Other,
    otherPreferredProducts: formData.investmentPreference.otherPreferredProducts.trim() === "" && formData.investmentPreference.preferredProductsId === PreferredProducts.Other,

    riskAwarenessQuestions: 
    ((formData.investmentPreference.isRetailInvestor !== null  && formData.investmentPreference.isRetailInvestor)  || 
    (formData.investmentPreference.isProfessionalInvestor !== null && formData.investmentPreference.isProfessionalInvestor)) && (
      formData.investmentPreference.hasHighLiquidityRiskUnderstand === null ||
      formData.investmentPreference.hasInvestmentLossUnderstand === null ||
      formData.investmentPreference.hasUncertainPayoutsUnderstand === null ||
      formData.investmentPreference.hasWithdrawalRightsUnderstand === null),

    investorLimitsQuestions: 
    ((formData.investmentPreference.isRetailInvestor !== null  && formData.investmentPreference.isRetailInvestor)  || 
    (formData.investmentPreference.isProfessionalInvestor !== null && formData.investmentPreference.isProfessionalInvestor)) && (
      formData.investmentPreference.canInvestProfessionalLimit === null ||
      formData.investmentPreference.canInvestRetailLimit === null ||
      formData.investmentPreference.canInvestRetailOfferingLimit === null ||
      formData.investmentPreference.hasLimitsWithdrawalRightsUnderstand === null),

    confirmationsQuestions: 
    ((formData.investmentPreference.isRetailInvestor !== null  && formData.investmentPreference.isRetailInvestor)  || 
    (formData.investmentPreference.isProfessionalInvestor !== null && formData.investmentPreference.isProfessionalInvestor)) && (
      formData.investmentPreference.hasRiskComprehensionConfirmation === null ||
      formData.investmentPreference.hasDocumentReviewedConfirmation === null ||
      formData.investmentPreference.hasContentsUnderstoodConfirmation === null ||
      formData.investmentPreference.hasProfessionalLimitConfirmation === null ||
      formData.investmentPreference.hasRetailOfferingLimitConfirmation === null ||
      formData.investmentPreference.hasRetailLimitConfirmation === null),
      
    riskAwarnessConfirmation:
    ((formData.investmentPreference.isRetailInvestor !== null  && formData.investmentPreference.isRetailInvestor)  || 
    (formData.investmentPreference.isProfessionalInvestor !== null && formData.investmentPreference.isProfessionalInvestor))
    && formData.investmentPreference.isInfoAcknowledgmentConfirmed === false

  };
  return newTouched;
}; 

export default InvestmentPreference;
